import * as React from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { OfferEventTransferService } from '../OfferEventTransferService';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { getPlatformValue } from '../../../utils/Functions';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import {
    OfferEventTransferOffer,
    OfferEventTransferPreview,
    OfferEventTransferRequest,
} from '../../../model/offer/OfferEventTransfer';

const initialForm = { sourceOfferId: '', targetOfferId: '', eventIds: '' };

const offerLabel = (offer: OfferEventTransferOffer) =>
    `#${offer.id} - ${offer.campaignTitle ?? '-'} - ${offer.userDisplayName ?? '-'} (${offer.status}) `;

const spendingLabel = (offer: OfferEventTransferOffer) => (Number(offer.isLongTerm) ? 'Spending' : '');

export function OfferEventTransfer() {
    const [form, setForm] = useState(initialForm);
    const [preview, setPreview] = useState<OfferEventTransferPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [sourceSearch, setSourceSearch] = useState('');
    const [sourceOptions, setSourceOptions] = useState<OfferEventTransferOffer[]>([]);
    const [targetOptions, setTargetOptions] = useState<OfferEventTransferOffer[]>([]);

    useEffect(() => {
        OfferEventTransferService.getSourceOffers(sourceSearch)
            .then(offers =>
                setSourceOptions(offers.map(o => ({ ...o, label: offerLabel(o), spending: spendingLabel(o) })))
            )
            .catch(ErrorToast);
    }, [sourceSearch]);

    useEffect(() => {
        setTargetOptions([]);
        if (!form.sourceOfferId) return;
        OfferEventTransferService.getTargetOffers(Number(form.sourceOfferId)).then(setTargetOptions).catch(ErrorToast);
    }, [form.sourceOfferId]);

    const buildRequest = (): OfferEventTransferRequest | null => {
        const sourceOfferId = Number(form.sourceOfferId);
        const targetOfferId = Number(form.targetOfferId);
        if (!sourceOfferId || !targetOfferId) {
            ErrorToast({ response: { data: { message: 'Source and target offer ID are required' } } });
            return null;
        }
        const eventIds = form.eventIds
            .split(/[\s,;]+/)
            .filter(Boolean)
            .map(Number);
        if (eventIds.some(id => !Number.isInteger(id) || id <= 0)) {
            ErrorToast({ response: { data: { message: 'Event IDs must be numbers' } } });
            return null;
        }
        return { sourceOfferId, targetOfferId, ...(eventIds.length ? { eventIds } : {}) };
    };

    const handleChange = (evt: React.ChangeEvent<any>) => {
        const { name, value } = evt.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setPreview(null);
    };

    const handlePreview = () => {
        const request = buildRequest();
        if (!request) return;
        setLoading(true);
        OfferEventTransferService.preview(request)
            .then(setPreview)
            .catch(ErrorToast)
            .finally(() => setLoading(false));
    };

    const handleTransfer = () => {
        const request = buildRequest();
        if (!request) return;
        if (!window.confirm(`Move ${preview?.mapping.length} event(s) to offer ${request.targetOfferId}?`)) return;
        setLoading(true);
        OfferEventTransferService.transfer(request)
            .then(result => {
                SuccessToast(`Moved ${result.movedEvents} event(s) and ${result.movedReportItems} report item(s)`);
                setForm(initialForm);
                setPreview(null);
            })
            .catch(ErrorToast)
            .finally(() => setLoading(false));
    };

    return (
        <Card>
            <Card.Header>
                <Card.Title>Transfer events between offers</Card.Title>
            </Card.Header>
            <Card.Body>
                <div className="row mb-5">
                    <div className="col-md-3 col-6">
                        <Form.Label>Source offer</Form.Label>
                        <AutocompleteInput
                            placeholder="Offer ID, campaign or influencer"
                            onSelect={(offer: OfferEventTransferOffer) => {
                                setForm(prev => ({ ...prev, sourceOfferId: String(offer.id), targetOfferId: '' }));
                                setPreview(null);
                            }}
                            setFilter={setSourceSearch}
                            value={sourceSearch}
                            items={sourceOptions}
                            hasClearBtn
                            renderItems={['label', 'spending']}
                            highlightKeys={['spending']}
                            clearFunc={() => {
                                setForm(prev => ({ ...prev, sourceOfferId: '', targetOfferId: '' }));
                                setSourceSearch('');
                                setPreview(null);
                            }}
                        />
                    </div>
                    <div className="col-md-3 col-6">
                        <Form.Label>Target offer (same influencer)</Form.Label>
                        <SingleSelectDropdown
                            placeholder={form.sourceOfferId ? 'Select target offer' : 'Select source offer first'}
                            options={targetOptions.map(o => ({
                                value: String(o.id),
                                label: offerLabel(o),
                                highlight: spendingLabel(o),
                            }))}
                            selected={form.targetOfferId}
                            onChange={value => {
                                setForm(prev => ({ ...prev, targetOfferId: value }));
                                setPreview(null);
                            }}
                        />
                    </div>
                    <div className="col-md-6">
                        <Form.Label>Event IDs (optional, comma separated, empty = all)</Form.Label>
                        <Form.Control name="eventIds" value={form.eventIds} onChange={handleChange} />
                    </div>
                </div>

                <Button onClick={handlePreview} disabled={loading} className="mr-3">
                    Preview
                </Button>
                <Button
                    variant="danger"
                    onClick={handleTransfer}
                    disabled={loading || !preview?.canTransfer}
                    data-cy="offer-event-transfer-submit"
                >
                    Transfer
                </Button>

                {preview && (
                    <div className="mt-8">
                        {preview.blockers.map(blocker => (
                            <Alert key={blocker} variant="danger">
                                {blocker}
                            </Alert>
                        ))}
                        {preview.missingOfferItems.length > 0 && (
                            <Alert variant="warning">
                                No free offer item on the target offer for:{' '}
                                {preview.missingOfferItems
                                    .map(
                                        m =>
                                            `event ${m.eventId} (${getPlatformValue(m.platform)} / ${m.type}): ${
                                                m.reason
                                            }`
                                    )
                                    .join(', ')}
                            </Alert>
                        )}
                        {preview.mapping.length > 0 && (
                            <>
                                <p>
                                    {preview.mapping.length} event(s), {preview.reportItemsCount} report item(s) will be
                                    moved.
                                </p>
                                <Table striped bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th>Event</th>
                                            <th>Platform</th>
                                            <th>Type</th>
                                            <th>From offer item</th>
                                            <th>To offer item</th>
                                            <th>New position</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.mapping.map(m => (
                                            <tr key={m.eventId}>
                                                <td>{m.eventId}</td>
                                                <td>{getPlatformValue(m.platform)}</td>
                                                <td>{m.type}</td>
                                                <td>{m.fromOfferItemId}</td>
                                                <td>{m.toOfferItemId}</td>
                                                <td>{m.platformPosition}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}
